import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { Send, X, Bot, User } from 'lucide-react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isTyping?: boolean;
}

interface ChatAgentProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChatAgent({ isOpen, onClose }: ChatAgentProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasVisited, setHasVisited] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = hasVisited 
        ? "Hey, welcome back! Good to see you again. Looking for something specific today, or just browsing my latest work?"
        : "Hey there! 👋 Welcome to my design world! I'm here to help you navigate my portfolio and find exactly what you're looking for. Want to see my case studies, learn about my process, or just chat about design? Let's dive in!";
      
      addBotMessage(welcomeMessage);
      setHasVisited(true);
    }
  }, [isOpen, hasVisited, messages.length]);

  const addBotMessage = (text: string) => {
    setIsTyping(true);
    setTimeout(() => {
      const newMessage: Message = {
        id: Date.now().toString(),
        text,
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, newMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const addUserMessage = (text: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    
    addUserMessage(inputValue);
    const response = generateResponse(inputValue.toLowerCase());
    setInputValue('');
    addBotMessage(response);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const generateResponse = (userInput: string): string => {
    // Quick navigation responses
    if (userInput.includes('case study') || userInput.includes('case studies')) {
      return "Awesome choice! I have several case studies that showcase different aspects of my design thinking. Are you more interested in:\n\n• Mobile app design\n• Web applications\n• Design systems\n• User research projects\n\nOr would you like to see my most recent work?";
    }

    if (userInput.includes('process') || userInput.includes('how you work')) {
      return "I love talking about process! I typically start with understanding the problem through user research, then move into ideation and prototyping. I'm big on iterative design and testing with real users. Want me to walk you through a specific project to see this in action?";
    }

    if (userInput.includes('about') || userInput.includes('background')) {
      return "I've been designing for a few years now, working with everything from scrappy startups to larger tech companies. I especially love projects where I can really dig into user research and create something that genuinely improves people's day-to-day. What kind of project brought you here?";
    }

    if (userInput.includes('contact') || userInput.includes('connect') || userInput.includes('hire')) {
      return "Oh exciting, you're looking for a designer! I'd love to hear more about what you're working on. Are you looking for someone full-time, or is this a freelance project? Either way, let's chat - drop me a line through the contact form, or feel free to grab time on my calendar. Coffee chat, anyone? ☕";
    }

    if (userInput.includes('tools') || userInput.includes('figma') || userInput.includes('software')) {
      return "I'm a bit of a tool nerd! My go-to stack is Figma for design and prototyping, Figjam for workshopping, and I sometimes jump into Principle or After Effects for those fancy micro-interactions. But honestly, the best tool is the one that gets the job done, right? What tools do you work with?";
    }

    if (userInput.includes('menu') || userInput.includes('options') || userInput.includes('what can you show')) {
      return "Here's what I can show you:\n\n💼 Case Studies - Deep dives into my favorite projects\n🎨 Design Work - UI designs, prototypes, and visual explorations\n🛠 My Process - How I approach design challenges\n👤 About Me - My background and what makes me tick\n📫 Let's Connect - Ways to get in touch\n\nWhat catches your eye?";
    }

    if (userInput.includes('react') || userInput.includes('portfolio') || userInput.includes('website')) {
      return "You found the chat! Fun fact: I actually designed this chat experience myself - pretty meta, right? It's running on React with TypeScript, and yes, I did user test it with other designers. 😄";
    }

    if (userInput.includes('feedback') || userInput.includes('thoughts') || userInput.includes('opinion')) {
      return "You're checking out my portfolio pretty thoroughly - I love that! If you have any feedback on the experience or the work itself, I'm always looking to improve. Designers gotta stick together, right?";
    }

    if (userInput.includes('hello') || userInput.includes('hi') || userInput.includes('hey')) {
      return "Hey there! 👋 Great to meet you! I'm here to help you explore my work. What brings you to my portfolio today?";
    }

    if (userInput.includes('thanks') || userInput.includes('thank you')) {
      return "You're so welcome! Happy to help. Is there anything else you'd like to know about my work or process?";
    }

    if (userInput.includes('bye') || userInput.includes('goodbye') || userInput.includes('see you')) {
      return "Thanks for stopping by! Feel free to reach out if you want to chat more about design or have any questions. Have a great day! 👋";
    }

    // Default responses for various topics
    if (userInput.includes('mobile') || userInput.includes('app')) {
      return "Mobile design is where I really shine! I love the challenge of creating intuitive experiences on smaller screens. Are you interested in iOS, Android, or cross-platform solutions?";
    }

    if (userInput.includes('web') || userInput.includes('website') || userInput.includes('ui')) {
      return "Web design is fascinating because you're designing for so many different contexts - desktop, tablet, mobile. I focus heavily on responsive design and accessibility. What kind of web project are you working on?";
    }

    if (userInput.includes('research') || userInput.includes('user research') || userInput.includes('ux')) {
      return "User research is the foundation of everything I do! I love getting into the weeds with user interviews, usability testing, and data analysis. It's amazing how much insight you can get from talking to real users. Have you done much user research in your work?";
    }

    // Fallback responses
    const fallbackResponses = [
      "That's really interesting! Tell me more about what you're thinking.",
      "I'd love to hear more about that. What's your experience been like?",
      "That's a great question! Let me think about the best way to explain that...",
      "You know, that's something I think about a lot. What's your take on it?",
      "I'm curious about your perspective on that. What brought you to that conclusion?"
    ];

    return fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
  };

  const quickActions = [
    { label: "Case Studies", action: () => addBotMessage(generateResponse("case studies")) },
    { label: "My Process", action: () => addBotMessage(generateResponse("process")) },
    { label: "About Me", action: () => addBotMessage(generateResponse("about")) },
    { label: "Let's Connect", action: () => addBotMessage(generateResponse("contact")) },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[600px] z-50">
      <Card className="h-full flex flex-col shadow-2xl border-2">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 bg-primary text-primary-foreground">
          <div className="flex items-center space-x-2">
            <Bot className="h-5 w-5" />
            <h3 className="font-semibold">Charlie's Assistant</h3>
            <Badge variant="secondary" className="text-xs">Online</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-primary-foreground hover:bg-primary-foreground/20">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="flex-1 flex flex-col p-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start space-x-2 max-w-[80%] ${message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    message.sender === 'user' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {message.sender === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div className={`rounded-lg px-3 py-2 ${
                    message.sender === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    <p className="text-sm whitespace-pre-line">{message.text}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="bg-muted text-muted-foreground rounded-lg px-3 py-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-current rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 1 && (
            <div className="p-4 border-t">
              <p className="text-xs text-muted-foreground mb-2">Quick actions:</p>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={action.action}
                    className="text-xs"
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1"
              />
              <Button onClick={handleSendMessage} size="sm" disabled={!inputValue.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
